import { detectBrowserTranslation, isAffectedReactError } from '@/utils/browserTranslation';
import type { PropsWithChildren, ReactNode } from 'react';
import { Component } from 'react';

interface IState {
    attempt: number;
    displayErrorPage: boolean;
}

interface IProps {
    // If return true, ErrorBoundary will retry by re-mounting children, rather than switching to fallback page
    fallback: ReactNode;
}

// There is no react hook for ComponentDidCatch yet
export class ErrorBoundary extends Component<PropsWithChildren<IProps>, IState> {
    public readonly state: Readonly<IState> = {
        attempt: 0,
        displayErrorPage: false,
    };

    public componentDidCatch(error: Error) {
        const results = detectBrowserTranslation() && isAffectedReactError(error.message);
        if (this.state.attempt === 0 || results) {
            // Quietly retry by changing the state and re-rendering the same children again
            // Childrens' states and focus may be lost, but may be better than outright crashing the page
            this.setState({ attempt: this.state.attempt + 1 });
        } else {
            // Sometimes the message is another object.
            if (typeof error?.message === 'object') {
                console.error(`[componentDidCatch] - ${JSON.stringify(error.message)}`, { error });
            } else {
                console.error(`[componentDidCatch] ${error.message}`, { error });
            }
            this.setState({ displayErrorPage: true });
        }
    }

    public render() {
        if (this.state.displayErrorPage) {
            return this.props.fallback;
        }

        return this.props.children;
    }
}
